import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, Link } from '@tanstack/react-router';
import {
  Badge,
  Button,
  Card,
} from '@projexn/ui';
import {
  TENDER_STAGES,
  TENDER_STAGE_LABELS,
  type TenderStage,
} from '@projexn/shared';
import { api } from '../lib/api';

interface Tender {
  id: string;
  name: string;
  stage: TenderStage;
  description: string | null;
  valueCents: number | null;
  dueDate: string | null;
  clientId: string | null;
  metadata?: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

function refOf(t: Tender): string {
  const r = t.metadata?.reference;
  return typeof r === 'string' ? r : t.id.slice(-6).toUpperCase();
}

interface Quote {
  id: string;
  version: number;
  status: string;
  totalCents: number;
}

interface Rfi {
  id: string;
  number: number;
  subject: string;
  status: string;
  askedAt: string;
}

interface SiteVisit {
  id: string;
  scheduledAt: string;
  status: string;
}

export function TenderDetailPage() {
  const params = useParams({ strict: false }) as { tenderId: string };
  const tenderId = params.tenderId;
  const qc = useQueryClient();

  const tenderQ = useQuery({
    queryKey: ['tenders', tenderId],
    queryFn: () => api.get<Tender>(`/api/tenders/${tenderId}`),
  });
  const quotesQ = useQuery({
    queryKey: ['quotes', { tenderId }],
    queryFn: () => api.get<{ items: Quote[] }>(`/api/quotes?tenderId=${tenderId}`),
  });
  const rfisQ = useQuery({
    queryKey: ['rfis', { tenderId }],
    queryFn: () => api.get<{ items: Rfi[] }>(`/api/rfis?tenderId=${tenderId}`),
  });
  const visitsQ = useQuery({
    queryKey: ['site-visits', { tenderId }],
    queryFn: () => api.get<{ items: SiteVisit[] }>(`/api/site-visits?tenderId=${tenderId}`),
  });

  const advance = useMutation({
    mutationFn: (stage: TenderStage) =>
      api.patch(`/api/tenders/${tenderId}/stage`, { stage }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tenders'] }),
  });

  if (tenderQ.isLoading) {
    return <Card className="p-6 text-sm text-zinc-500">Loading…</Card>;
  }
  if (tenderQ.isError) {
    return (
      <Card className="p-6 text-sm text-red-700">
        {(tenderQ.error as Error).message}
      </Card>
    );
  }
  const t = tenderQ.data!;

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <Link to="/pipeline" className="text-xs text-zinc-500 hover:underline">
            ← Pipeline
          </Link>
          <h1 className="mt-1 flex items-center gap-3 text-xl font-semibold">
            {t.name} <Badge>{refOf(t)}</Badge>
          </h1>
        </div>
        <select
          value={t.stage}
          onChange={(e) => advance.mutate(e.target.value as TenderStage)}
          className="h-9 rounded-md border border-zinc-300 bg-white px-3 text-sm"
        >
          {TENDER_STAGES.map((s) => (
            <option key={s} value={s}>
              {TENDER_STAGE_LABELS[s]}
            </option>
          ))}
        </select>
      </header>

      <section className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <Card className="p-4">
          <p className="text-xs uppercase tracking-wide text-zinc-500">Estimated value</p>
          <p className="mt-1 text-lg font-semibold">
            {t.valueCents !== null ? `$${(t.valueCents / 100).toLocaleString()}` : '—'}
          </p>
        </Card>
        <Card className="p-4">
          <p className="text-xs uppercase tracking-wide text-zinc-500">Due</p>
          <p className="mt-1 text-lg font-semibold">
            {t.dueDate ? new Date(t.dueDate).toLocaleDateString() : '—'}
          </p>
        </Card>
        <Card className="p-4">
          <p className="text-xs uppercase tracking-wide text-zinc-500">Last updated</p>
          <p className="mt-1 text-lg font-semibold">
            {new Date(t.updatedAt).toLocaleString()}
          </p>
        </Card>
      </section>

      {t.description && (
        <Card className="p-4">
          <h2 className="text-sm font-semibold text-zinc-700">Description</h2>
          <p className="mt-2 whitespace-pre-wrap text-sm text-zinc-900">{t.description}</p>
        </Card>
      )}

      <section className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <RelatedList title="Quotes" items={quotesQ.data?.items ?? []} render={(q) => (
          <div className="text-sm">
            <span className="font-medium">v{q.version}</span>
            <span className="ml-2 text-xs text-zinc-500">{q.status}</span>
            <span className="ml-auto float-right">${(q.totalCents / 100).toLocaleString()}</span>
          </div>
        )} />
        <RelatedList title="RFIs" items={rfisQ.data?.items ?? []} render={(r) => (
          <div className="text-sm">
            <p className="font-medium">#{r.number} {r.subject}</p>
            <span className="text-xs text-zinc-500">{r.status}</span>
          </div>
        )} />
        <RelatedList title="Site visits" items={visitsQ.data?.items ?? []} render={(v) => (
          <div className="text-sm">
            <p>{new Date(v.scheduledAt).toLocaleString()}</p>
            <span className="text-xs text-zinc-500">{v.status}</span>
          </div>
        )} />
      </section>

      <div>
        <Button variant="ghost" onClick={() => qc.invalidateQueries({ queryKey: ['tenders', tenderId] })}>
          Refresh
        </Button>
      </div>
    </div>
  );
}

function RelatedList<T extends { id: string }>({
  title,
  items,
  render,
}: {
  title: string;
  items: T[];
  render: (item: T) => React.ReactNode;
}) {
  return (
    <Card className="p-4">
      <h2 className="mb-2 flex items-center justify-between text-sm font-semibold text-zinc-700">
        {title}
        <span className="text-xs font-normal text-zinc-500">{items.length}</span>
      </h2>
      {items.length === 0 ? (
        <p className="text-xs text-zinc-500">None.</p>
      ) : (
        <ul className="divide-y divide-zinc-100">
          {items.map((it) => (
            <li key={it.id} className="py-2">
              {render(it)}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
